"use client";

import { File, FileImage, FileSpreadsheet, FileText, X } from "lucide-react";
import { useId, useRef, useState, type ChangeEvent, type DragEvent } from "react";

import { Button } from "./button";
import { Card } from "./card";
import { cn } from "./utils";

export interface FileInputProps {
    id?: string;
    accept?: string;
    /** MIME types used for validation when a file is selected. */
    acceptedMimeTypes?: string[];
    acceptHint?: string;
    maxSizeBytes?: number;
    value?: File | null;
    defaultValue?: File | null;
    onChange?: (file: File | null) => void;
    onInvalidFile?: (message: string) => void;
    className?: string;
    disabled?: boolean;
}

const DEFAULT_MAX_SIZE_BYTES = 10 * 1024 * 1024;

function formatFileSize(bytes: number) {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Number.parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
}

function getFileIcon(file: File) {
    const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
    if (["csv", "xlsx", "xls"].includes(extension)) {
        return <FileSpreadsheet className="w-5 h-5 text-foreground" aria-hidden="true" />;
    }
    if (["pdf"].includes(extension)) {
        return <FileText className="w-5 h-5 text-foreground" aria-hidden="true" />;
    }
    if (file.type.startsWith("image/") || ["jpg", "jpeg", "png", "webp", "heic"].includes(extension)) {
        return <FileImage className="w-5 h-5 text-foreground" aria-hidden="true" />;
    }
    return <File className="w-5 h-5 text-foreground" aria-hidden="true" />;
}

function isAcceptedFile(file: File, acceptedMimeTypes?: string[]) {
    if (!acceptedMimeTypes?.length) return true;
    const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
    return acceptedMimeTypes.some((type) => {
        if (type.endsWith("/*")) {
            return file.type.startsWith(type.replace("/*", "/"));
        }
        if (type.startsWith(".")) {
            return `.${extension}` === type;
        }
        return file.type === type;
    });
}

export function FileInput({
    id,
    accept,
    acceptedMimeTypes,
    acceptHint = "Accepted file types: PDF, JPG, PNG, CSV, XLSX or XLS.",
    maxSizeBytes = DEFAULT_MAX_SIZE_BYTES,
    value,
    defaultValue = null,
    onChange,
    onInvalidFile,
    className,
    disabled = false,
}: FileInputProps) {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const fileInputRef = useRef<HTMLInputElement>(null);
    const isControlled = value !== undefined;
    const [internalFile, setInternalFile] = useState<File | null>(defaultValue);
    const selectedFile = isControlled ? value : internalFile;

    const setFile = (file: File | null) => {
        if (!isControlled) {
            setInternalFile(file);
        }
        onChange?.(file);
    };

    const rejectFile = (message: string) => {
        onInvalidFile?.(message);
    };

    const handleFile = (file: File | undefined) => {
        if (!file || disabled) return;

        if (!isAcceptedFile(file, acceptedMimeTypes)) {
            rejectFile(acceptHint);
            return;
        }

        if (file.size > maxSizeBytes) {
            rejectFile(`File must be smaller than ${formatFileSize(maxSizeBytes)}.`);
            return;
        }

        setFile(file);
    };

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        handleFile(event.target.files?.[0]);
    };

    const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
        event.preventDefault();
        if (disabled) return;
        handleFile(event.dataTransfer.files?.[0]);
    };

    const resetFile = () => {
        setFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    return (
        <div className={cn("w-full", className)}>
            <button
                type="button"
                disabled={disabled}
                className={cn(
                    "flex justify-center px-6 py-8 border border-input border-dashed rounded-md w-full",
                    disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
                )}
                onDragOver={(event) => event.preventDefault()}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
            >
                <div className="text-center">
                    <File className="mx-auto w-10 h-10 text-muted-foreground" aria-hidden="true" />
                    <p className="mt-2 text-muted-foreground text-sm leading-6">
                        Drag and drop or <span className="font-medium text-primary">choose file</span>
                    </p>
                </div>
            </button>
            <input
                id={inputId}
                ref={fileInputRef}
                type="file"
                className="sr-only"
                accept={accept}
                disabled={disabled}
                onChange={handleFileChange}
            />

            <p className="mt-2 text-muted-foreground text-xs leading-5">
                <span>{acceptHint}</span>
                <span className="block sm:inline sm:ml-2">Max. size: {formatFileSize(maxSizeBytes)}</span>
            </p>

            {selectedFile ? (
                <Card className="relative gap-4 bg-muted shadow-none mt-4 p-4">
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="top-1 right-1 absolute text-muted-foreground hover:text-foreground"
                        aria-label="Remove file"
                        onClick={resetFile}
                        disabled={disabled}
                    >
                        <X className="w-5 h-5 shrink-0" aria-hidden="true" />
                    </Button>

                    <div className="flex items-center space-x-2.5">
                        <span className="flex flex-shrink-0 justify-center items-center bg-background shadow-sm ring-border rounded-sm ring-1 ring-inset w-10 h-10">
                            {getFileIcon(selectedFile)}
                        </span>
                        <div className="pr-8 min-w-0">
                            <p className="font-medium text-foreground text-xs truncate">{selectedFile.name}</p>
                            <p className="mt-0.5 text-muted-foreground text-xs">{formatFileSize(selectedFile.size)}</p>
                        </div>
                    </div>
                </Card>
            ) : null}
        </div>
    );
}
